import React, { useState, useEffect, useRef } from 'react';
import { CartItem, Category } from '../types';
import { Portal } from './Portal';
import { useTranslation } from 'react-i18next';
import { createDineInOrder } from '../services/dineInOrderService';
import { createTakeawayOrder } from '../services/takeawayOrderService';
import { OrderConfirmation } from './OrderConfirmation';
import { PaymentProcessor } from './PaymentProcessor';
import { MinimumOrderModal } from './MinimumOrderModal';
import Lottie from 'lottie-react';
import leftHandAnimation from '../a/lotie1.json';
import { Menu, ShoppingCart, Settings, AlertCircle, X, ChevronRight, ArrowRight, ArrowLeft, Fullscreen, Minimize, Delete, User, CreditCard, Wallet, ShoppingBag, Minus, Plus, Trash2, Edit3 } from 'lucide-react';
import { getStoreInfo, getStoreData } from '../services/api';
import { aggregateCustomizationOptions } from '../utils/ticketFormatter';
import { settingsStorage } from '../services/settingsStorage';
// v5 fidélité: helper de formatage + config admin
import { formatCustomerPhone } from '../services/customerHelpers';
import { getLoyaltySettings, DEFAULT_LOYALTY, type LoyaltySettings } from '../services/loyaltySettings';
// v5 fidélité redeem: UI
// Note: redeemLoyaltyPoints n'est plus nécessaire car apply_loyalty_discount
// déduit maintenant les points atomiquement.
import { LoyaltyStepContent, LoyaltyDiscountRecap } from './LoyaltyStepContent';
// v5: bouton Commander personnalisable
import OrderButton from './OrderButton';
import {
  getOrderButtonDesign,
  DEFAULT_ORDER_BUTTON,
  type OrderButtonConfig,
} from '../services/orderButtonDesign';

interface CartProps {
  items: CartItem[];
  categories?: Category[];
  onUpdateQuantity: (uniqueId: string, quantity: number) => void;
  onRemoveItem: (uniqueId: string) => void;
  onClearCart?: () => void;
  onCheckout: () => void;
  onEditItem?: (item: CartItem) => void;
  printSettings?: 'manual' | 'auto' | 'none';
  isWarningActive?: boolean;
  warningCountdown?: number;
  /** v5 Mode Rush: appelé si create_order est refusé avec reason="rush_active" */
  onRushRejected?: (status: { active: boolean; remaining_seconds: number; message: string }) => void;
  /** v5 Happy Hour: somme des prix NORMAUX des articles éligibles (0 = pas de happy hour) */
  happyHourEligibleAmount?: number;
  /** v5 Modules: appelé si create_order est refusé avec reason="module_disabled" */
  onModuleDisabled?: (description: string) => void;
}

interface CartItem {
  menuItem: {
    id: string;
    name: string;
    price: number;
    description?: string;
    image?: string;
  };
  quantity: number;
  customizations: { [key: string]: { id: string; name: string; price: number }[] };
  uniqueId: string;
}

const calculateItemPrice = (item: CartItem) => {
  const basePrice = item.menuItem.price;
  const optionsPrice = Object.entries(item.customizations).reduce((acc, [_, options]) => {
    return acc + options.reduce((sum, opt) => sum + (opt.price || 0), 0);
  }, 0);
  const unitPrice = basePrice + optionsPrice;
  const totalPrice = unitPrice * item.quantity;
  return { unitPrice, totalPrice };
};

const getAdjustedTotal = (items: CartItem[]): number => {
  return items.reduce((sum, item) => {
    const { totalPrice } = calculateItemPrice(item);
    return sum + totalPrice;
  }, 0);
};

export const Cart: React.FC<CartProps> = ({
  items,
  categories = [],
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onEditItem,
  printSettings = 'manual',
  onCheckout,
  isWarningActive = false,
  warningCountdown = 0,
  onRushRejected,
  happyHourEligibleAmount = 0,
  onModuleDisabled,
}) => {
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [orderType, setOrderType] = useState<'takeaway' | 'dine-in' | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | null>(null);
  const [customerName, setCustomerName] = useState<string>('B-Nom non indiqué');
  const [tableNumber, setTableNumber] = useState<string>('1');
  const [displayCustomerName, setDisplayCustomerName] = useState<string>('');
  const [displayTableNumber, setDisplayTableNumber] = useState<string>('');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showLetterKeyboard, setShowLetterKeyboard] = useState(false);
  const [showNumberKeyboard, setShowNumberKeyboard] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [itemNotes, setItemNotes] = useState<{ [key: string]: string }>({});
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [showNoteKeyboard, setShowNoteKeyboard] = useState(false);
  const [activeField, setActiveField] = useState<'name' | 'table' | 'phone' | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentStep, setCurrentStep] = useState<'type' | 'info' | 'loyalty' | 'payment'>('type');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPaymentProcessor, setShowPaymentProcessor] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [adjustedTotal, setAdjustedTotal] = useState<number | null>(null);
  const [invoiceData, setInvoiceData] = useState<any | null>(null);
  const [cartResult, setCartResult] = useState<any | null>(null);

  // v5 fidélité: téléphone client optionnel + config admin
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [displayCustomerPhone, setDisplayCustomerPhone] = useState<string>('');
  const [loyaltySettings, setLoyaltySettings] = useState<LoyaltySettings>(DEFAULT_LOYALTY);

  // v5 fidélité redeem: états pour l'utilisation des points
  const [loyaltyPointsUsed, setLoyaltyPointsUsed] = useState<number>(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState<number>(0);
  const [redeemDone, setRedeemDone] = useState<boolean>(false); // évite double-redeem
  // v5 fidélité: message affiché si apply_loyalty_discount échoue (réduction non appliquée)
  const [loyaltyApplyFailedMessage, setLoyaltyApplyFailedMessage] = useState<string>('');
  useEffect(() => {
    let mounted = true;
    getLoyaltySettings().then((s) => {
      if (mounted) setLoyaltySettings(s);
    });
    const onUpdate = (e: Event) => {
      const ce = e as CustomEvent<LoyaltySettings>;
      if (ce.detail) setLoyaltySettings(ce.detail);
    };
    window.addEventListener('loyaltySettingsUpdated', onUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('loyaltySettingsUpdated', onUpdate);
    };
  }, []);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showMinimumOrderModal, setShowMinimumOrderModal] = useState(false);
  const [minimumOrderInfo, setMinimumOrderInfo] = useState({ minimum: 0, current: 0 });

  const [paymentSettings, setPaymentSettings] = useState({
    enableCardPayment: true,
    enableCashPayment: true,
    autoSubmitIfSinglePayment: false, // v5
  });

  // v5: design personnalisable du bouton Commander
  const [orderBtnDesign, setOrderBtnDesign] = useState<OrderButtonConfig>(DEFAULT_ORDER_BUTTON);
  useEffect(() => {
    let mounted = true;
    getOrderButtonDesign().then((d) => {
      if (mounted) setOrderBtnDesign(d);
    });
    const onUpdate = (e: Event) => {
      const ce = e as CustomEvent<OrderButtonConfig>;
      if (ce.detail) setOrderBtnDesign(ce.detail);
    };
    window.addEventListener('orderButtonDesignUpdated', onUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('orderButtonDesignUpdated', onUpdate);
    };
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const isEmpty = totalItems === 0;
  const total = getAdjustedTotal(items);
  const { t } = useTranslation();

  // Optimisation: État pour visibilité onglet (pause Lottie si caché)
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  // Optimisation: Gérer visibilité onglet pour Lottie
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const loadPaymentSettings = async () => {
      try {
        const settings = await settingsStorage.getPaymentSettings();
        setPaymentSettings(settings);
        console.log('[Cart] Paramètres de paiement chargés depuis IndexedDB:', settings);
      } catch (error) {
        console.error('[Cart] Erreur chargement paramètres paiement:', error);
      }
    };
    loadPaymentSettings();
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        setShowScrollIndicator(scrollTop + clientHeight < scrollHeight - 1);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScroll);
      setTimeout(checkScroll, 100);
    }

    return () => {
      if (scrollContainer) scrollContainer.removeEventListener('scroll', checkScroll);
    };
  }, [items]);

  useEffect(() => {
    setAdjustedTotal(null);
  }, [items]);

  const handleQuantityUpdate = (uniqueId: string, newQuantity: number) => {
    if (!uniqueId) {
      console.error('Erreur: uniqueId est undefined dans handleQuantityUpdate');
      return;
    }
    if (newQuantity < 1) {
      handleRemoveItem(uniqueId);
      return;
    }
    onUpdateQuantity(uniqueId, newQuantity);
  };

  const handleRemoveItem = (uniqueId: string) => {
    if (!uniqueId) {
      console.error('Erreur: uniqueId est undefined dans handleRemoveItem');
      return;
    }
    onRemoveItem(uniqueId);
  };

  const clearCart = () => {
    if (onClearCart) {
      onClearCart();
    } else {
      items.forEach((item) => onRemoveItem(item.uniqueId));
    }
  };

  const resetOrderModal = () => {
    setOrderType(null);
    setPaymentMethod(null);
    setCustomerName('B-Nom non indiqué');
    setTableNumber('1');
    setDisplayCustomerName('');
    setDisplayTableNumber('');
    setCustomerPhone(''); // v5 fidélité: reset si annulation
    setDisplayCustomerPhone(''); // v5 fidélité
    // v5 fidélité redeem: reset si annulation
    setLoyaltyPointsUsed(0);
    setLoyaltyDiscount(0);
    setRedeemDone(false);
    setLoyaltyApplyFailedMessage(''); // v5 fidélité: reset message
    setShowOrderForm(false);
    setShowPaymentForm(false);
    setShowLetterKeyboard(false);
    setShowNumberKeyboard(false);
    setActiveField(null);
    setSubmitError(null);
    setCurrentStep('type');
    setShowPaymentProcessor(false);
    setAdjustedTotal(null);
    setCartResult(null);
    setInvoiceData(null); // v5 BUG FIX: éviter réutilisation order_payment_id sur prochaine commande
  };

  const dismissOrderModal = () => {
    setShowOrderTypeModal(false);
    // Ne pas réinitialiser les états ni vider le panier
  };

  const closeOrderModal = () => {
    setShowOrderTypeModal(false);
    resetOrderModal();
    clearCart(); // Vider le panier
  };

  const handleOrderTypeSelection = async (type: 'takeaway' | 'dine-in') => {
    // v5 BUG FIX (cart_id réutilisé): TOUJOURS partir d'un panier propre côté API.
    // Si la commande précédente a réussi/échoué et que l'utilisateur en relance une,
    // on force un cart_id + order_payment_id frais en nettoyant l'état.
    setCartResult(null);
    setInvoiceData(null);
    setSubmitError(null);

    setIsTransitioning(true);
    setOrderType(type);
    requestAnimationFrame(() => {
      setCurrentStep('info');
      setShowOrderForm(true);
      setShowPaymentForm(false);
      if (type === 'takeaway') {
        setActiveField('name');
        setShowLetterKeyboard(true);
        setShowNumberKeyboard(false);
      } else {
        setActiveField('table');
        setShowNumberKeyboard(true);
        setShowLetterKeyboard(false);
      }
      setIsTransitioning(false);
    });
  };

  const getPaymentMethodLabel = (method: string | null) => {
    switch (method) {
      case 'cash':
        return 'Au comptoir';
      case 'card':
        return 'Carte';
      default:
        return 'Non spécifié';
    }
  };

  const handlePaymentMethodSelection = async (method: 'cash' | 'card') => {
    setPaymentMethod(method);
    // v5: si on choisit cash, on s'assure que le PaymentProcessor card est fermé
    if (method === 'cash') {
      setShowPaymentProcessor(false);
    }

    if (method === 'card') {
      if (!orderType) {
        setSubmitError(t('Veuillez sélectionner un type de commande avant de payer par carte'));
        setPaymentMethod(null);
        return;
      }
      setIsSubmitting(true);
      try {
        setShowPaymentProcessor(true);
      } catch (error) {
        setSubmitError(t('Erreur lors du calcul du montant à payer'));
        setPaymentMethod(null);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const paymentNote = 'Paiement au comptoir';
      setItemNotes((prev) => ({ ...prev, payment_method: paymentNote }));
      // v5: ne montrer la confirmation QUE si la commande a réussi.
      const success = await handleSubmit('cash');
      if (success) {
        setShowConfirmation(true);
      }
    }
  };

  // === v5: fonction dédiée d'auto-submit cash ===
  // Évite les problèmes de stale state (itemNotes pas encore propagé)
  // en passant directement le payment_method à handleSubmit via une note manuelle.
  // v5 fix: accepte cartResult/invoiceData/adjustedTotal en paramètres pour éviter
  // le stale state (React batching après les setState)
  const handleAutoSubmitCash = async (
    overrideCartResult?: any,
    overrideInvoiceData?: any,
    overrideAdjustedTotal?: number
  ): Promise<boolean> => {
    console.log('[v5] handleAutoSubmitCash() démarré', {
      orderType,
      hasOverrideCart: !!overrideCartResult,
      hasOverrideInvoice: !!overrideInvoiceData,
      overrideAdjustedTotal,
      hasStateCart: !!cartResult,
      hasStateInvoice: !!invoiceData,
      stateAdjustedTotal: adjustedTotal,
    });

    if (!orderType) {
      console.error('[v5] handleAutoSubmitCash: orderType manquant');
      setSubmitError(t('Type de commande manquant'));
      return false;
    }

    setPaymentMethod('cash');
    setSubmitError(null);

    // Note de paiement
    const paymentNote = 'Paiement au comptoir';
    setItemNotes((prev) => ({ ...prev, payment_method: paymentNote }));

    // v5: on passe les overrides à handleSubmit
    const success = await handleSubmit(
      'cash',
      overrideCartResult,
      overrideInvoiceData,
      overrideAdjustedTotal
    );
    console.log('[v5] handleAutoSubmitCash → success =', success);
    if (success) {
      setShowConfirmation(true);
    }
    return success;
  };

  const handlePaymentSuccess = async () => {
    const paymentNote = 'Paiement par carte effectué';
    setItemNotes((prev) => ({ ...prev, payment_method: paymentNote }));
    setShowPaymentProcessor(false);
    setPaymentMethod('card');
    // v5 BUG FIX CRITIQUE: AWAIT + ne montrer la confirmation QUE si succès
    // Sans ça, setShowConfirmation s'exécutait avant handleSubmit → race condition
    // possible avec un invoiceData stale qui fuite vers la commande suivante.
    const success = await handleSubmit('card_machine');
    if (success) {
      setShowConfirmation(true);
    }
    // En cas d'échec, handleSubmit a déjà reset cartResult + invoiceData
    // → la prochaine tentative repartira propre.
  };

  const handlePaymentCancel = () => {
    setPaymentMethod(null);
    setShowPaymentProcessor(false);
    setSubmitError(t('Paiement annulé. Veuillez choisir un mode de paiement pour réessayer ou annuler la commande.'));
    setCurrentStep('payment');
    setIsSubmitting(false);
  };

  const handlePaymentError = (error: string) => {
    setSubmitError(
      t('Erreur de paiement : {{error}}. Veuillez réessayer ou choisir un autre mode de paiement.', { error })
    );
    setPaymentMethod(null);
    setShowPaymentProcessor(false);
    setCurrentStep('payment');
    setIsSubmitting(false);
  };

  const handleSubmit = async (
    paymentMode: string = 'cash',
    // v5: overrides optionnels pour éviter le stale state quand on appelle
    // handleSubmit juste après un setCartResult/setInvoiceData (React batching)
    overrideCartResult?: any,
    overrideInvoiceData?: any,
    overrideAdjustedTotal?: number
  ): Promise<boolean> => {
    if (orderType === 'dine-in' && !tableNumber.trim()) {
      setTableNumber('1');
    }

    setIsSubmitting(true);
    setSubmitError(null);

    // v5: utiliser les overrides si fournis, sinon le state (comportement legacy)
    const effectiveCartResult = overrideCartResult ?? cartResult;
    const effectiveInvoiceData = overrideInvoiceData ?? invoiceData;
    const effectiveAdjustedTotal =
      overrideAdjustedTotal ?? (adjustedTotal || total);

    // v5 DIAGNOSTIC (bug cart_id / order_payment_id réutilisé): log complet
    // pour tracer si une commande réutilise des IDs d'une commande précédente.
    console.log('[NF525 DIAG] === Nouvelle commande ===', {
      cart_id: effectiveCartResult?.cart_id || '(NOUVEAU, sera créé)',
      order_payment_id: effectiveInvoiceData?.order_payment?._id || '(NOUVEAU, sera créé)',
      paymentMode,
      itemCount: items.length,
      total: effectiveAdjustedTotal,
      timestamp: new Date().toISOString(),
    });

    try {
      let response;
      if (orderType === 'dine-in') {
        response = await createDineInOrder(
          items,
          tableNumber,
          customerName,
          effectiveAdjustedTotal,
          itemNotes,
          effectiveCartResult,
          effectiveInvoiceData,
          paymentMode,
          customerPhone, // v5 fidélité
          loyaltyDiscount, // v5 fidélité redeem
          loyaltyPointsUsed, // v5 fidélité redeem
          happyHourEligibleAmount // v5 happy hour
        );
      } else {
        response = await createTakeawayOrder(
          items,
          customerName,
          effectiveAdjustedTotal,
          itemNotes,
          effectiveCartResult,
          effectiveInvoiceData,
          paymentMode,
          customerPhone, // v5 fidélité
          loyaltyDiscount, // v5 fidélité redeem
          loyaltyPointsUsed, // v5 fidélité redeem
          happyHourEligibleAmount // v5 happy hour
        );
      }

      console.log('Réponse create_order:', JSON.stringify(response, null, 2));

      if (response.success) {
        setAdjustedTotal(response.displayTotal);
        setShowOrderTypeModal(false);
        setShowConfirmation(true);

        // v5 fidélité: détecter si la réduction a échoué côté backend
        const loyaltyFailed = response.loyalty_apply_failed === true;
        const loyaltyFailReason = response.loyalty_fail_reason || '';
        const appliedDiscount = response.loyalty_applied_discount ?? 0;
        const appliedPoints = response.loyalty_applied_points ?? 0;

        if (loyaltyFailed) {
          // v5: apply_loyalty_discount a échoué → client a payé plein tarif
          // Message adapté selon la raison
          console.warn('[LoyaltyRedeem] Réduction non appliquée, message au client', {
            reason: loyaltyFailReason,
          });
          if (loyaltyFailReason === 'points_already_used') {
            // Cas spécifique : un autre kiosk/canal a déjà utilisé les points
            setLoyaltyApplyFailedMessage(
              t("Vos points ont déjà été utilisés sur une autre commande. Votre commande continue normalement au prix plein.")
            );
          } else {
            // Cas générique
            setLoyaltyApplyFailedMessage(
              t("Désolé, votre réduction fidélité n'a pas pu être appliquée. Voyez avec la caisse pour la fidélité.")
            );
          }
        }

        setInvoiceData((prev) => ({
          ...prev,
          ...response,
          order_id: response.order_id || prev?.order_payment?._id,
          // v5 fidélité: stocker la réduction appliquée
          loyalty_discount: appliedDiscount,
          loyalty_points_used: appliedPoints,
          // v5 happy hour: montants OFFICIELS du serveur (pour le ticket, NF525)
          happy_hour_discount: response.happy_hour_discount ?? 0,
          happy_hour_name: response.happy_hour_name || '',
          happy_hour_percent: response.happy_hour_percent ?? 0,
        }));

        // v5 happy hour: le serveur a refusé la remise (happy hour terminé pendant
        // la composition du panier). On informe le client, le total a été recalculé.
        if (response.happy_hour_expired) {
          console.warn('[HappyHour] Happy hour terminé pendant la commande → message client');
          setLoyaltyApplyFailedMessage(
            t('Le happy hour vient de se terminer. Votre commande a été recalculée au prix normal.')
          );
        }

        // v5 fidélité (NOUVEAU COMPORTEMENT ATOMIQUE) :
        // apply_loyalty_discount a DÉJÀ déduit les points atomiquement.
        // PLUS d'appel à redeem_loyalty_points ici (sinon double déduction).
        if (appliedDiscount > 0 && appliedPoints > 0) {
          console.log('[LoyaltyRedeem] Points déjà déduits par apply_loyalty_discount (atomique). Pas d\'appel redeem nécessaire.');
        }

        return true; // v5: succès
      } else {
        setAdjustedTotal(response.displayTotal || total);
        setSubmitError(response.message || t('Une erreur est survenue lors de la commande'));
        // v5 BUG FIX (cart_id réutilisé): si la commande a échoué côté backend
        // (ex: error 2003 "cart already used"), on RESET cartResult + invoiceData
        // pour qu'au prochain essai un NOUVEAU panier soit créé.
        setCartResult(null);
        setInvoiceData(null);

        // v5 Modules: le module kiosk a été désactivé par l'admin Restomi.
        // On remonte à App pour afficher l'écran de blocage dédié.
        if (response.module_disabled && onModuleDisabled) {
          onModuleDisabled(response.module_error_description || response.message || '');
        }

        // v5 Mode Rush: le backend a refusé la commande car le restaurant est en pause.
        // On remonte à App pour afficher l'écran de blocage avec message + compteur.
        if (response.rush_active && onRushRejected) {
          onRushRejected({
            active: true,
            remaining_seconds: response.rush_remaining_seconds ?? 0,
            message: response.rush_message || response.message || '',
          });
        }
        return false; // v5: échec
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      setSubmitError(error instanceof Error ? error.message : t('Une erreur est survenue'));
      setAdjustedTotal(total);
      // v5 BUG FIX (cart_id réutilisé): pareil pour les exceptions
      setCartResult(null);
      setInvoiceData(null);
      return false; // v5: échec
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setItemNotes({});
    setCustomerName('B-Nom non indiqué');
    setTableNumber('1');
    setDisplayCustomerName('');
    setDisplayTableNumber('');
    setCustomerPhone(''); // v5 fidélité: reset après commande
    setDisplayCustomerPhone(''); // v5 fidélité
    // v5 fidélité redeem: reset après commande
    setLoyaltyPointsUsed(0);
    setLoyaltyDiscount(0);
    setRedeemDone(false);
    setLoyaltyApplyFailedMessage(''); // v5 fidélité: reset message
    setOrderType(null);
    setPaymentMethod(null);
    setShowOrderTypeModal(false);
    setAdjustedTotal(null);
    setCartResult(null);
    setInvoiceData(null); // v5 BUG FIX: éviter réutilisation order_payment_id
    setSubmitError(null);
    clearCart();
    onCheckout();
  };

  const handleFieldFocus = (field: 'name' | 'table' | 'phone') => {
    setActiveField(field);
    if (field === 'name') {
      setShowLetterKeyboard(true);
      setShowNumberKeyboard(false);
    } else {
      setShowLetterKeyboard(false);
      setShowNumberKeyboard(true);
    }
  };

  const handleNumberPress = (number: string) => {
    // v5 fidélité: si le champ téléphone est actif, on tape dedans
    if (activeField === 'phone') {
      if (number === 'backspace') {
        setCustomerPhone((prev) => {
          const newValue = prev.slice(0, -1);
          setDisplayCustomerPhone(newValue);
          return newValue;
        });
        return;
      }
      if (number === 'clear') {
        setCustomerPhone('');
        setDisplayCustomerPhone('');
        return;
      }
      // Max 10 chiffres (format français 06XXXXXXXX)
      if (customerPhone.length < 10) {
        setCustomerPhone((prev) => {
          const newValue = prev + number;
          setDisplayCustomerPhone(newValue);
          return newValue;
        });
      }
      return;
    }

    // Sinon : comportement existant (numéro de table)
    if (number === 'backspace') {
      setTableNumber((prev) => {
        const newValue = prev.slice(0, -1);
        setDisplayTableNumber(newValue);
        return newValue;
      });
      return;
    }
    if (number === 'clear') {
      setTableNumber('');
      setDisplayTableNumber('');
      return;
    }
    if (tableNumber.length < 3) {
      setTableNumber((prev) => {
        const newValue = prev + number;
        setDisplayTableNumber(newValue);
        return newValue;
      });
    }
  };

  const handleLetterPress = (letter: string) => {
    if (letter === 'backspace') {
      setCustomerName((prev) => {
        const newValue = prev === 'B-Nom non indiqué' ? '' : prev.slice(0, -1);
        setDisplayCustomerName(newValue);
        return newValue;
      });
      return;
    }
    if (letter === 'clear') {
      setCustomerName('');
      setDisplayCustomerName('');
      return;
    }
    if (letter === 'space') {
      setCustomerName((prev) => {
        const newValue = prev === 'B-Nom non indiqué' ? ' ' : prev + ' ';
        setDisplayCustomerName(newValue);
        return newValue;
      });
      return;
    }
    if (customerName.length < 30 || customerName === 'B-Nom non indiqué') {
      setCustomerName((prev) => {
        const newValue = prev === 'B-Nom non indiqué' ? letter : prev + letter;
        setDisplayCustomerName(newValue);
        return newValue;
      });
    }
  };

  const handleNoteChange = (uniqueId: string, note: string) => {
    setItemNotes((prev) => ({ ...prev, [uniqueId]: note }));
  };

  const handleNoteKeyPress = (letter: string) => {
    if (!activeNoteId) return;
    if (letter === 'backspace') {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: (prev[activeNoteId] || '').slice(0, -1) }));
      return;
    }
    if (letter === 'clear') {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: '' }));
      return;
    }
    if (letter === 'space') {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: (prev[activeNoteId] || '') + ' ' }));
      return;
    }
    if ((itemNotes[activeNoteId] || '').length < 200) {
      setItemNotes((prev) => ({ ...prev, [activeNoteId]: (prev[activeNoteId] || '') + letter }));
    }
  };

  const openNoteKeyboard = (uniqueId: string) => {
    setActiveNoteId(uniqueId);
    setShowNoteKeyboard(true);
  };

  const closeNoteKeyboard = () => {
    setActiveNoteId(null);
    setShowNoteKeyboard(false);
  };

  const letterRows = [
    ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
    ['W', 'X', 'C', 'V', 'B', 'N'],
  ];

  const numberRows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'backspace'],
  ];

  const handleNextStep = async (skipLoyaltyCheck: boolean = false) => {
    setIsTransitioning(true);
    requestAnimationFrame(async () => {
      if (currentStep === 'type') {
        setCurrentStep('info');
        setShowOrderForm(true);
        setShowPaymentForm(false);
      } else if (currentStep === 'info' || currentStep === 'loyalty') {
        // v5 fidélité: 'info' ET 'loyalty' mènent au même traitement.
        // - depuis 'info' : on évalue si on doit passer par 'loyalty' d'abord
        // - depuis 'loyalty' : skipLoyaltyCheck=true → on saute direct à la création cart+invoice
        // Pas de transition intermédiaire visible → pas de clignotement.
        // v5 BUG FIX CRITIQUE (order_payment_id réutilisé):
        // Avant de créer un nouveau cart + invoice, on JETTE les anciens.
        // Sans ça, si l'utilisateur revient à l'étape 'info' depuis 'payment'
        // (handlePreviousStep), ou si une commande précédente a laissé un état,
        // on réutiliserait le même cart_id / order_payment_id → fiscalité KO.
        console.log('[NF525] handleNextStep → reset cart/invoice AVANT création', {
          oldCartId: cartResult?.cart_id,
          oldOrderPaymentId: invoiceData?.order_payment?._id,
        });
        setCartResult(null);
        setInvoiceData(null);
        setAdjustedTotal(null);

        if (orderType === 'dine-in' && !tableNumber.trim()) {
          setTableNumber('1');
        }

        setIsSubmitting(true);
        setSubmitError(null);

        // v5 fidélité REDEEM: ouvrir le popup dédié si toutes les conditions ADMIN sont OK
        // Conditions pour aller vers l'étape "loyalty" intégrée au cadre :
        //  - admin a activé la demande de téléphone
        //  - admin a activé l'utilisation des points
        //  - le client a saisi un téléphone
        //  - on n'est pas en train de revenir d'une décision (skipLoyaltyCheck=true)
        const formattedPhone = formatCustomerPhone(customerPhone);

        // 🔍 DIAGNOSTIC: log explicite de toutes les conditions
        console.log('[LoyaltyRedeem DIAG] Vérification des conditions du popup:', {
          skipLoyaltyCheck,
          phonePromptEnabled: loyaltySettings.phonePromptEnabled,
          redeemEnabled: loyaltySettings.redeemEnabled,
          customerPhoneRaw: customerPhone,
          customerPhoneFormatted: formattedPhone,
          willGoToLoyaltyStep:
            !skipLoyaltyCheck &&
            loyaltySettings.phonePromptEnabled &&
            loyaltySettings.redeemEnabled &&
            !!formattedPhone,
        });

        if (
          !skipLoyaltyCheck &&
          loyaltySettings.phonePromptEnabled &&
          loyaltySettings.redeemEnabled &&
          formattedPhone
        ) {
          console.log('[LoyaltyRedeem] ✅ Transition vers étape loyalty (intégrée au cadre)');
          setCurrentStep('loyalty');
          setIsSubmitting(false);
          setIsTransitioning(false);
          return; // on s'arrête ici, l'étape loyalty reprendra le flow via onConfirm/onSkip
        } else {
          console.log('[LoyaltyRedeem] ⏭️ Étape loyalty sautée. Raison:', {
            skipLoyaltyCheck: skipLoyaltyCheck ? '❌ déjà checké' : '✅ pas encore checké',
            phonePromptEnabled: loyaltySettings.phonePromptEnabled ? '✅ ON' : '❌ OFF en admin',
            redeemEnabled: loyaltySettings.redeemEnabled ? '✅ ON' : '❌ OFF en admin',
            formattedPhone: formattedPhone ? `✅ "${formattedPhone}"` : '❌ pas de téléphone saisi',
          });
        }

        let storeInfo = getStoreInfo();
        if (!storeInfo) {
          setSubmitError(t('Informations du store non disponibles'));
          setIsSubmitting(false);
          setIsTransitioning(false);
          return;
        }

        try {
          await getStoreData(storeInfo.store_id, storeInfo.server_token);
          storeInfo = getStoreInfo();
          console.log('storeInfo rafraichi avec les dernieres valeurs de TVA');
        } catch (refreshError) {
          console.warn('Impossible de rafraichir les infos du store, utilisation du cache:', refreshError);
        }

        console.log('storeInfo:', {
          is_tax_included: storeInfo.is_tax_included,
          is_use_item_tax: storeInfo.is_use_item_tax,
        });

        try {
          const formattedCustomerName = customerName.startsWith('Borne-')
            ? customerName
            : `Borne-${customerName}`;
          const addressParts = storeInfo.address.split(',');
          const city = addressParts.length > 1 ? addressParts[1].trim() : '';

          const handleTaxIncludedAndItemTaxTrue = async () => {
            let totalHT = 0;
            let totalItemTax = 0;

            const itemsWithTax = items.map((item) => {
              const itemPriceTTC = item.menuItem.price;
              const customizationPriceTTC = Object.values(item.customizations).reduce(
                (sum, options) => sum + options.reduce((optSum, opt) => optSum + opt.price, 0),
                0
              );
              const totalItemPriceTTC = (itemPriceTTC + customizationPriceTTC) * item.quantity;
              const itemTaxRate = item.menuItem.tax_details?.[0]?.tax || item.menuItem.tax || 0;
              const totalItemPriceHT = totalItemPriceTTC / (1 + itemTaxRate / 100);
              const itemTax = totalItemPriceTTC - totalItemPriceHT;

              totalHT += totalItemPriceHT;
              totalItemTax += itemTax;

              return {
                ...item,
                totalItemPriceHT,
                itemTax,
                itemTaxRate,
                customizationPrice: customizationPriceTTC,
              };
            });

            const cartData = {
              cart_id: '',
              cart_unique_token: storeInfo.server_token,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              destination_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'destination',
                  city: storeInfo.address.split(',')[1]?.trim() || '',
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    country_phone_code: storeInfo.country_phone_code,
                    email: '',
                    first_name: '',
                    image_url: '',
                    last_name: '',
                    name:
                      orderType === 'dine-in'
                        ? `Borne-${customerName} - Table ${tableNumber}`
                        : `Borne-${customerName}`,
                    phone: formatCustomerPhone(customerPhone), // v5 fidélité
                    user_type: 7,
                  },
                },
              ],
              id: storeInfo.store_id,
              is_guest_order: true,
              is_tax_included: true,
              is_use_item_tax: true,
              order_details: [
                {
                  items: itemsWithTax.map((item) => ({
                    item_id: item.menuItem.id,
                    unique_id: Math.floor(Math.random() * 1000),
                    item_name: item.menuItem.name,
                    quantity: item.quantity,
                    details: item.menuItem.description || '',
                    image_url: [item.menuItem.image || ''],
                    item_price: item.menuItem.price,
                    note_for_item: itemNotes[item.uniqueId] || '',
                    specifications: Object.entries(item.customizations).map(([specName, options]) => ({
                      list: options.map((opt) => ({
                        _id: `${item.menuItem.id}-${specName}-${opt.id}`,
                        main_specification_id: null,
                        franchise_id: null,
                        name: opt.name,
                        price: opt.price,
                        quantity: 1,
                        is_default_selected: false,
                        is_user_selected: true,
                      })),
                      name: specName,
                      price: options.reduce((sum, opt) => sum + opt.price, 0),
                      type: 1,
                    })),
                    tax: item.itemTaxRate,
                    tax_details: item.menuItem.tax_details || [],
                    total_item_price: item.totalItemPriceHT,
                    total_item_tax: item.itemTax,
                    total_specification_tax:
                      item.itemTax * (item.customizationPrice / (item.menuItem.price + item.customizationPrice)),
                    product_id: item.menuItem.id,
                    product_name: item.menuItem.name,
                  })),
                  product_id: items[0]?.menuItem.id,
                  product_name: items[0]?.menuItem.name,
                  total_item_price: totalHT,
                  total_item_tax: totalItemTax,
                  unique_id: Math.floor(Math.random() * 1000),
                },
              ],
              pickup_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'pickup',
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    name: storeInfo.name,
                    email: storeInfo.email,
                    phone: storeInfo.phone,
                    country_phone_code: storeInfo.country_phone_code,
                    image_url: storeInfo.image_url,
                    user_type: 2,
                  },
                },
              ],
              store_id: storeInfo.store_id,
              table_no: orderType === 'dine-in' ? tableNumber : '0',
              timezone: 'Europe/Paris',
              total_cart_price: totalHT + totalItemTax,
              total_item_tax: totalItemTax,
              user_id: '',
              user_type: 2,
            };

            const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(cartData),
            });
            if (!cartResponse.ok) throw new Error('Erreur lors de l’ajout au panier');
            const cartResult = await cartResponse.json();
            if (!cartResult.success) throw new Error(cartResult.message || 'Erreur lors de l’ajout au panier');

            const invoiceData = {
              cart_id: cartResult.cart_id || '',
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              id: storeInfo.store_id,
              is_guest_order: true,
              is_store_place_order: orderType === 'dine-in',
              is_tax_included: true,
              is_use_item_tax: true,
              is_user_pick_up_order: orderType === 'takeaway',
              order_type: 2,
              store_id: storeInfo.store_id,
              timezone: 'Europe/Paris',
              total_cart_amout_without_tax: totalHT + totalItemTax,
              total_cart_price: totalHT,
              total_distance: 0,
              total_item_count: items.length,
              total_item_tax: totalItemTax,
              total_time: 0,
              user_id: cartResult.user_id || '',
              table_no: orderType === 'dine-in' ? tableNumber : undefined,
            };

            const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(invoiceData),
            });
            if (!invoiceResponse.ok) throw new Error('Erreur lors de la vérification de la facture');
            const invoiceResult = await invoiceResponse.json();
            console.log('Réponse get_order_cart_invoice:', JSON.stringify(invoiceResult, null, 2));
            console.log('[NF525] NOUVEAU order_payment._id reçu:', invoiceResult?.order_payment?._id);

            if (!invoiceResult.success) {
              if (invoiceResult.error_code === 557 && invoiceResult.min_order_price) {
                console.log('[MINIMUM ORDER ERROR] Montant minimum non atteint:', {
                  minimum: invoiceResult.min_order_price,
                  current: total,
                  difference: invoiceResult.min_order_price - total
                });
                setMinimumOrderInfo({
                  minimum: invoiceResult.min_order_price,
                  current: total
                });
                // Fermer le popup d'informations avant d'afficher le popup de minimum de commande
                setShowOrderTypeModal(false);
                // Attendre que l'animation de fermeture se termine avant d'ouvrir le nouveau popup
                setTimeout(() => {
                  setShowMinimumOrderModal(true);
                }, 300);
                throw new Error('MINIMUM_ORDER_NOT_MET');
              }
              throw new Error(invoiceResult.message || 'Erreur lors de la vérification de la facture');
            }

            return { cartResult, invoiceResult };
          };

          const handleTaxIncludedAndItemTaxFalse = async () => {
            const totalHT = total;
            const generalTaxRate = storeInfo.general_tax / 100;
            const generalTaxAmount = totalHT * generalTaxRate / (1 + generalTaxRate);
            const totalCartPrice = totalHT - generalTaxAmount;

            const cartData = {
              cart_id: '',
              cart_unique_token: storeInfo.server_token,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              destination_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'destination',
                  city,
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    country_phone_code: storeInfo.country_phone_code,
                    email: '',
                    first_name: '',
                    image_url: '',
                    last_name: '',
                    name: orderType === 'dine-in' ? `${formattedCustomerName} - Table ${tableNumber}` : formattedCustomerName,
                    phone: formatCustomerPhone(customerPhone), // v5 fidélité
                    user_type: 7,
                  },
                },
              ],
              id: storeInfo.store_id,
              is_guest_order: true,
              is_tax_included: true,
              is_use_item_tax: false,
              order_details: [
                {
                  items: items.map((item) => ({
                    item_id: item.menuItem.id,
                    unique_id: Math.floor(Math.random() * 1000),
                    item_name: item.menuItem.name,
                    quantity: item.quantity,
                    details: item.menuItem.description || '',
                    image_url: [item.menuItem.image || ''],
                    item_price: item.menuItem.price,
                    note_for_item: itemNotes[item.uniqueId] || '',
                    specifications: Object.entries(item.customizations).map(([specId, options]) => ({
                      list: options.map((opt) => ({
                        _id: `${item.menuItem.id}-${specId}-${opt.id}`,
                        main_specification_id: null,
                        franchise_id: null,
                        name: opt.name,
                        price: opt.price,
                        quantity: 1,
                        is_default_selected: false,
                        is_user_selected: true,
                      })),
                      name: specId,
                      price: options.reduce((sum, opt) => sum + opt.price, 0),
                      type: 1,
                    })),
                    tax: 0,
                    tax_details: [],
                    total_item_price: item.menuItem.price * item.quantity,
                    total_item_tax: 0,
                    total_specification_tax: 0,
                    product_id: item.menuItem.id,
                    product_name: item.menuItem.name,
                  })),
                  product_id: items[0]?.menuItem.id,
                  product_name: items[0]?.menuItem.name,
                  total_item_price: totalHT,
                  total_item_tax: 0,
                  unique_id: Math.floor(Math.random() * 1000),
                },
              ],
              pickup_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'pickup',
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    name: storeInfo.name,
                    email: storeInfo.email,
                    phone: storeInfo.phone,
                    country_phone_code: storeInfo.country_phone_code,
                    image_url: storeInfo.image_url,
                    user_type: 2,
                  },
                },
              ],
              store_id: storeInfo.store_id,
              table_no: orderType === 'dine-in' ? tableNumber : '0',
              timezone: 'Europe/Paris',
              total_cart_price: totalHT,
              total_item_tax: 0,
              user_id: '',
              user_type: 2,
            };

            const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(cartData),
            });
            if (!cartResponse.ok) throw new Error('Erreur lors de l’ajout au panier');
            const cartResult = await cartResponse.json();
            if (!cartResult.success) throw new Error(cartResult.message || 'Erreur lors de l’ajout au panier');

            const invoiceData = {
              cart_id: cartResult.cart_id,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              id: storeInfo.store_id,
              is_guest_order: true,
              is_store_place_order: orderType === 'dine-in',
              is_tax_included: true,
              is_use_item_tax: false,
              is_user_pick_up_order: orderType === 'takeaway',
              order_type: 2,
              store_id: storeInfo.store_id,
              timezone: 'Europe/Paris',
              total_cart_amout_without_tax: totalHT,
              total_cart_price: totalCartPrice,
              total_distance: 0,
              total_item_count: items.length,
              total_item_tax: 0,
              total_time: 0,
              user_id: cartResult.user_id || '',
              table_no: orderType === 'dine-in' ? tableNumber : undefined,
            };

            const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(invoiceData),
            });
            if (!invoiceResponse.ok) throw new Error('Erreur lors de la vérification de la facture');
            const invoiceResult = await invoiceResponse.json();
            console.log('Réponse get_order_cart_invoice:', JSON.stringify(invoiceResult, null, 2));
            console.log('[NF525] NOUVEAU order_payment._id reçu:', invoiceResult?.order_payment?._id);
            if (!invoiceResult.success) throw new Error(invoiceResult.message || 'Erreur lors de la vérification de la facture');

            return { cartResult, invoiceResult };
          };

          const handleTaxNotIncludedAndItemTaxTrue = async () => {
            let totalHT = 0;
            let totalItemTax = 0;

            const itemsWithTax = items.map((item) => {
              const itemPriceHT = item.menuItem.price;
              const customizationPriceHT = Object.values(item.customizations).reduce(
                (sum, options) => sum + options.reduce((optSum, opt) => optSum + opt.price, 0),
                0
              );
              const totalItemPriceHT = (itemPriceHT + customizationPriceHT) * item.quantity;
              const itemTaxRate = item.menuItem.tax_details?.[0]?.tax || item.menuItem.tax || 0;
              const totalItemPriceTTC = totalItemPriceHT * (1 + itemTaxRate / 100);
              const itemTax = totalItemPriceTTC - totalItemPriceHT;

              totalHT += totalItemPriceHT;
              totalItemTax += itemTax;

              return {
                ...item,
                totalItemPriceHT,
                itemTax,
                itemTaxRate,
                customizationPrice: customizationPriceHT,
              };
            });

            const cartData = {
              cart_id: '',
              cart_unique_token: storeInfo.server_token,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              destination_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'destination',
                  city: storeInfo.address.split(',')[1]?.trim() || '',
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    country_phone_code: storeInfo.country_phone_code,
                    email: '',
                    first_name: '',
                    image_url: '',
                    last_name: '',
                    name:
                      orderType === 'dine-in'
                        ? `Borne-${customerName} - Table ${tableNumber}`
                        : `Borne-${customerName}`,
                    phone: formatCustomerPhone(customerPhone), // v5 fidélité
                    user_type: 7,
                  },
                },
              ],
              id: storeInfo.store_id,
              is_guest_order: true,
              is_tax_included: false,
              is_use_item_tax: true,
              order_details: [
                {
                  items: itemsWithTax.map((item) => ({
                    item_id: item.menuItem.id,
                    unique_id: Math.floor(Math.random() * 1000),
                    item_name: item.menuItem.name,
                    quantity: item.quantity,
                    details: item.menuItem.description || '',
                    image_url: [item.menuItem.image || ''],
                    item_price: item.menuItem.price,
                    note_for_item: itemNotes[item.uniqueId] || '',
                    specifications: Object.entries(item.customizations).map(([specName, options]) => ({
                      list: options.map((opt) => ({
                        _id: `${item.menuItem.id}-${specName}-${opt.id}`,
                        main_specification_id: null,
                        franchise_id: null,
                        name: opt.name,
                        price: opt.price,
                        quantity: 1,
                        is_default_selected: false,
                        is_user_selected: true,
                      })),
                      name: specName,
                      price: options.reduce((sum, opt) => sum + opt.price, 0),
                      type: 1,
                    })),
                    tax: item.itemTaxRate,
                    tax_details: item.menuItem.tax_details || [],
                    total_item_price: item.totalItemPriceHT,
                    total_item_tax: item.itemTax,
                    total_specification_tax:
                      item.itemTax * (item.customizationPrice / (item.menuItem.price + item.customizationPrice)),
                    product_id: item.menuItem.id,
                    product_name: item.menuItem.name,
                  })),
                  product_id: items[0]?.menuItem.id,
                  product_name: items[0]?.menuItem.name,
                  total_item_price: totalHT,
                  total_item_tax: totalItemTax,
                  unique_id: Math.floor(Math.random() * 1000),
                },
              ],
              pickup_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'pickup',
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    name: storeInfo.name,
                    email: storeInfo.email,
                    phone: storeInfo.phone,
                    country_phone_code: storeInfo.country_phone_code,
                    image_url: storeInfo.image_url,
                    user_type: 2,
                  },
                },
              ],
              store_id: storeInfo.store_id,
              table_no: orderType === 'dine-in' ? tableNumber : '0',
              timezone: 'Europe/Paris',
              total_cart_price: totalHT + totalItemTax,
              total_item_tax: totalItemTax,
              user_id: '',
              user_type: 2,
            };

            const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(cartData),
            });
            if (!cartResponse.ok) throw new Error('Erreur lors de l’ajout au panier');
            const cartResult = await cartResponse.json();
            if (!cartResult.success) throw new Error(cartResult.message || 'Erreur lors de l’ajout au panier');

            const invoiceData = {
              cart_id: cartResult.cart_id || '',
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              id: storeInfo.store_id,
              is_guest_order: true,
              is_store_place_order: orderType === 'dine-in',
              is_tax_included: false,
              is_use_item_tax: true,
              is_user_pick_up_order: orderType === 'takeaway',
              order_type: 2,
              store_id: storeInfo.store_id,
              timezone: 'Europe/Paris',
              total_cart_amout_without_tax: totalHT + totalItemTax,
              total_cart_price: totalHT,
              total_distance: 0,
              total_item_count: items.length,
              total_item_tax: totalItemTax,
              total_time: 0,
              user_id: cartResult.user_id || '',
              table_no: orderType === 'dine-in' ? tableNumber : undefined,
            };

            const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(invoiceData),
            });
            if (!invoiceResponse.ok) throw new Error('Erreur lors de la vérification de la facture');
            const invoiceResult = await invoiceResponse.json();
            console.log('Réponse get_order_cart_invoice:', JSON.stringify(invoiceResult, null, 2));
            console.log('[NF525] NOUVEAU order_payment._id reçu:', invoiceResult?.order_payment?._id);
            if (!invoiceResult.success) throw new Error(invoiceResult.message || 'Erreur lors de la vérification de la facture');

            return { cartResult, invoiceResult };
          };

          const handleTaxNotIncludedAndItemTaxFalse = async () => {
            let totalHT = 0;

            const itemsWithTax = items.map((item) => {
              const itemPrice = item.menuItem.price;
              const customizationPrice = Object.values(item.customizations).reduce(
                (sum, options) => sum + options.reduce((optSum, opt) => optSum + opt.price, 0),
                0
              );
              const totalItemPriceHT = (itemPrice + customizationPrice) * item.quantity;

              totalHT += totalItemPriceHT;

              return { ...item, totalItemPriceHT, itemTax: 0, itemTaxRate: 0, customizationPrice };
            });

            const cartData = {
              cart_id: '',
              cart_unique_token: storeInfo.server_token,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              destination_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'destination',
                  city,
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    country_phone_code: storeInfo.country_phone_code,
                    email: '',
                    first_name: '',
                    image_url: '',
                    last_name: '',
                    name: orderType === 'dine-in' ? `${formattedCustomerName} - Table ${tableNumber}` : formattedCustomerName,
                    phone: formatCustomerPhone(customerPhone), // v5 fidélité
                    user_type: 7,
                  },
                },
              ],
              id: storeInfo.store_id,
              is_guest_order: true,
              is_tax_included: false,
              is_use_item_tax: false,
              order_details: [
                {
                  items: itemsWithTax.map((item) => ({
                    item_id: item.menuItem.id,
                    unique_id: Math.floor(Math.random() * 1000),
                    item_name: item.menuItem.name,
                    quantity: item.quantity,
                    details: item.menuItem.description || '',
                    image_url: [item.menuItem.image || ''],
                    item_price: item.menuItem.price,
                    note_for_item: itemNotes[item.uniqueId] || '',
                    specifications: Object.entries(item.customizations).map(([specId, options]) => ({
                      list: options.map((opt) => ({
                        _id: `${item.menuItem.id}-${specId}-${opt.id}`,
                        main_specification_id: null,
                        franchise_id: null,
                        name: opt.name,
                        price: opt.price,
                        quantity: 1,
                        is_default_selected: false,
                        is_user_selected: true,
                      })),
                      name: specId,
                      price: options.reduce((sum, opt) => sum + opt.price, 0),
                      type: 1,
                    })),
                    tax: 0,
                    tax_details: [],
                    total_item_price: item.totalItemPriceHT,
                    total_item_tax: 0,
                    total_specification_tax: 0,
                    product_id: item.menuItem.id,
                    product_name: item.menuItem.name,
                  })),
                  product_id: items[0]?.menuItem.id,
                  product_name: items[0]?.menuItem.name,
                  total_item_price: totalHT,
                  total_item_tax: 0,
                  unique_id: Math.floor(Math.random() * 1000),
                },
              ],
              pickup_addresses: [
                {
                  address: storeInfo.address,
                  address_type: 'pickup',
                  delivery_status: 0,
                  location: storeInfo.location,
                  note: '',
                  user_details: {
                    name: storeInfo.name,
                    email: storeInfo.email,
                    phone: storeInfo.phone,
                    country_phone_code: storeInfo.country_phone_code,
                    image_url: storeInfo.image_url,
                    user_type: 2,
                  },
                },
              ],
              store_id: storeInfo.store_id,
              table_no: orderType === 'dine-in' ? tableNumber : '0',
              timezone: 'Europe/Paris',
              total_cart_price: totalHT,
              total_item_tax: 0,
              user_id: '',
              user_type: 2,
            };

            const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(cartData),
            });
            if (!cartResponse.ok) throw new Error('Erreur lors de l’ajout au panier');
            const cartResult = await cartResponse.json();
            if (!cartResult.success) throw new Error(cartResult.message || 'Erreur lors de l’ajout au panier');

            const invoiceData = {
              cart_id: cartResult.cart_id,
              delivery_types: orderType === 'dine-in' ? 'in place' : '',
              id: storeInfo.store_id,
              is_guest_order: true,
              is_store_place_order: orderType === 'dine-in',
              is_tax_included: false,
              is_use_item_tax: false,
              is_user_pick_up_order: orderType === 'takeaway',
              order_type: 2,
              store_id: storeInfo.store_id,
              timezone: 'Europe/Paris',
              total_cart_amout_without_tax: totalHT,
              total_cart_price: totalHT,
              total_distance: 0,
              total_item_count: items.length,
              total_item_tax: 0,
              total_time: 0,
              user_id: cartResult.user_id || '',
              table_no: orderType === 'dine-in' ? tableNumber : undefined,
            };

            const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'server-token': storeInfo.server_token },
              body: JSON.stringify(invoiceData),
            });
            if (!invoiceResponse.ok) throw new Error('Erreur lors de la vérification de la facture');
            const invoiceResult = await invoiceResponse.json();
            console.log('Réponse get_order_cart_invoice:', JSON.stringify(invoiceResult, null, 2));
            console.log('[NF525] NOUVEAU order_payment._id reçu:', invoiceResult?.order_payment?._id);
            if (!invoiceResult.success) throw new Error(invoiceResult.message || 'Erreur lors de la vérification de la facture');

            return { cartResult, invoiceResult };
          };

          let result;
          if (storeInfo.is_tax_included && storeInfo.is_use_item_tax) {
            result = await handleTaxIncludedAndItemTaxTrue();
          } else if (storeInfo.is_tax_included && !storeInfo.is_use_item_tax) {
            result = await handleTaxIncludedAndItemTaxFalse();
          } else if (!storeInfo.is_tax_included && storeInfo.is_use_item_tax) {
            result = await handleTaxNotIncludedAndItemTaxTrue();
          } else {
            result = await handleTaxNotIncludedAndItemTaxFalse();
          }

          setCartResult(result.cartResult);
          setInvoiceData(result.invoiceResult);
          console.log('invoiceData après get_order_cart_invoice:', JSON.stringify(result.invoiceResult, null, 2));
          const orderPayment = result.invoiceResult.order_payment;
          const finalTotal = orderPayment?.total_order_price || orderPayment?.total || total;
          setAdjustedTotal(finalTotal);

          // v5: capturer les valeurs fraîches dans des variables locales
          // pour les passer en override à handleAutoSubmitCash (évite stale state)
          const freshCartResult = result.cartResult;
          const freshInvoiceData = result.invoiceResult;

          const hasPaymentMethods = paymentSettings.enableCardPayment || paymentSettings.enableCashPayment;
          // Détecte si l'auto-submit doit être déclenché
          const singleModeActive =
            paymentSettings.enableCashPayment !== paymentSettings.enableCardPayment;
          const shouldAutoSubmit =
            paymentSettings.autoSubmitIfSinglePayment && singleModeActive;
          const onlyMethod: 'cash' | 'card' = paymentSettings.enableCashPayment
            ? 'cash'
            : 'card';

          // === v5 DIAGNOSTIC LOGS ===
          console.log('[v5] === Décision de paiement ===', {
            enableCashPayment: paymentSettings.enableCashPayment,
            enableCardPayment: paymentSettings.enableCardPayment,
            autoSubmitIfSinglePayment: paymentSettings.autoSubmitIfSinglePayment,
            singleModeActive,
            shouldAutoSubmit,
            onlyMethod,
            orderType,
            hasFreshCartResult: !!freshCartResult,
            hasFreshInvoiceData: !!freshInvoiceData,
          });

          if (!hasPaymentMethods) {
            // Aucun mode activé : fallback cash silencieux et complet
            console.log('[v5] Aucun mode actif → fallback cash direct');
            setCurrentStep('payment');
            setShowOrderForm(false);
            setShowPaymentForm(true);
            setIsSubmitting(false);
            setIsTransitioning(false);
            // Passe les valeurs fraîches en override (évite stale state)
            setTimeout(async () => {
              const ok = await handleAutoSubmitCash(
                freshCartResult,
                freshInvoiceData,
                finalTotal
              );
              if (!ok) {
                console.warn('[v5] fallback cash a échoué');
              }
            }, 50);
            return;
          } else if (shouldAutoSubmit && onlyMethod === 'cash') {
            // === v5 cash auto: passe la commande sans popup ===
            console.log('[v5] Auto-submit cash → on lance handleAutoSubmitCash');
            // PRÉPARER l'état AVANT
            setCurrentStep('payment');
            setShowOrderForm(false);
            setShowPaymentForm(true);
            setIsSubmitting(false);
            setIsTransitioning(false);
            // Passe les valeurs fraîches en override (évite stale state)
            setTimeout(async () => {
              const ok = await handleAutoSubmitCash(
                freshCartResult,
                freshInvoiceData,
                finalTotal
              );
              if (!ok) {
                console.warn('[v5] auto-submit cash a échoué → l\'utilisateur voit l\'erreur');
              }
            }, 50);
            return;
          } else if (shouldAutoSubmit && onlyMethod === 'card') {
            // === v5 card auto: lance directement le TPE ===
            if (!orderType) {
              console.warn('[v5] Auto-submit card impossible : orderType non défini');
              setCurrentStep('payment');
              setShowOrderForm(false);
              setShowPaymentForm(true);
            } else {
              console.log('[v5] Auto-submit card → on lance le TPE');
              setCurrentStep('payment');
              setShowOrderForm(false);
              setShowPaymentForm(true);
              setIsSubmitting(false);
              setIsTransitioning(false);
              setTimeout(() => {
                setPaymentMethod('card');
                setShowPaymentProcessor(true);
              }, 50);
              return;
            }
          } else {
            // Flow normal : afficher le popup de sélection
            console.log('[v5] Flow normal → affichage popup paiement');
            setCurrentStep('payment');
            setShowOrderForm(false);
            setShowPaymentForm(true);
          }
        } catch (error) {
          console.error('Erreur:', error);
          setSubmitError(error instanceof Error ? error.message : t('Une erreur est survenue'));
          setAdjustedTotal(total);
        } finally {
          setIsSubmitting(false);
          setIsTransitioning(false);
        }
      }
    });
  };

  const handlePreviousStep = () => {
    setIsTransitioning(true);
    requestAnimationFrame(() => {
      if (currentStep === 'payment') {
        // v5 BUG FIX CRITIQUE: si l'user revient de 'payment' à 'info',
        // jeter le cart/invoice actuel pour qu'au prochain "Continuer"
        // un NOUVEAU cart_id + NOUVEAU order_payment_id soient créés.
        console.log('[NF525] handlePreviousStep → reset cart/invoice (retour arrière)');
        setCartResult(null);
        setInvoiceData(null);
        setAdjustedTotal(null);
        // Reset loyalty state aussi
        setLoyaltyDiscount(0);
        setLoyaltyPointsUsed(0);

        setCurrentStep('info');
        setShowPaymentForm(false);
        setShowOrderForm(true);
      } else if (currentStep === 'loyalty') {
        // v5 fidélité: depuis loyalty, revenir à info (sans avoir validé)
        console.log('[LoyaltyRedeem] handlePreviousStep depuis loyalty → retour à info');
        setLoyaltyDiscount(0);
        setLoyaltyPointsUsed(0);
        setCurrentStep('info');
      } else if (currentStep === 'info') {
        setCurrentStep('type');
        setShowOrderForm(false);
      }
      setIsTransitioning(false);
    });
  };

  console.log('Cart rendu - invoiceData avant OrderConfirmation:', JSON.stringify(invoiceData, null, 2));

  return (
    <div className="bg-white rounded-2xl shadow-xl flex flex-col h-full max-h-[calc(100vh-100px)]">
      <div className="flex-shrink-0 p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">{t('Votre commande')}</h2>
          <span className="flex items-center gap-2 text-gray-500">
            <ShoppingBag size={18} />
            <span className="font-medium">{totalItems}</span>
          </span>
        </div>
      </div>

      {isWarningActive && warningCountdown > 0 && (
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-500 p-2.5 sm:p-3 shadow-lg animate-pulse">
          <div className="flex items-center justify-center gap-2 text-white">
            <AlertCircle size={18} className="flex-shrink-0" />
            <p className="font-bold text-sm sm:text-base whitespace-nowrap">
              {t('Panier vidé dans')}
            </p>
            <div className="flex items-center gap-1 bg-white/20 px-2.5 py-1 rounded-lg backdrop-blur-sm flex-shrink-0">
              <span className="text-xl sm:text-2xl font-bold text-white leading-none">
                {warningCountdown}
              </span>
              <span className="text-xs text-white/90">
                {t('s')}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col relative">
        {isEmpty ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <ShoppingBag size={36} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium text-sm">{t('Votre panier est vide')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('Veuillez sélectionner au moins 1 élément')}</p>
            </div>
          </div>
        ) : (
          <>
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-3">
              {items.map((item) => {
                const { unitPrice, totalPrice } = calculateItemPrice(item);

                return (
                  <div
                    key={item.uniqueId}
                    className="flex flex-col gap-3 p-3 bg-gray-50 rounded-xl text-sm"
                  >
                    <div className="flex gap-3">
                      <div
                        className={`flex-1 min-w-0 ${onEditItem ? 'cursor-pointer hover:bg-gray-100 rounded-lg p-1 -m-1 transition-colors' : ''}`}
                        onClick={() => onEditItem && onEditItem(item)}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-800 truncate">{item.menuItem.name}</h3>
                          {onEditItem && (
                            <Edit3 size={14} className="text-orange-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-1">
                          {t('Prix unitaire:')} {unitPrice.toFixed(2)}
                        </div>
                        {Object.entries(item.customizations).map(([specId, options], optIndex) => {
                          const aggregated = aggregateCustomizationOptions(options);
                          return (
                            <div key={`${item.uniqueId}-${specId}-${optIndex}`} className="text-xs text-gray-500">
                              {aggregated.map((opt, i) => (
                                <span key={`${item.uniqueId}-${specId}-${opt.id}-${i}`}>
                                  {opt.name}
                                  {opt.quantity > 1 && ` x${opt.quantity}`}
                                  {opt.price > 0 && ` (+${(opt.price * opt.quantity).toFixed(2)}€)`}
                                  {i < aggregated.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <span className="font-semibold text-gray-800">{totalPrice.toFixed(2)}€</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRemoveItem(item.uniqueId)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleQuantityUpdate(item.uniqueId, item.quantity - 1)}
                        className="p-1 rounded-lg bg-white shadow-sm hover:bg-gray-100 transition-colors"
                        disabled={item.quantity <= 1}
                      >
                        <Minus size={14} className="text-gray-600" />
                      </button>
                      <span className="font-medium text-gray-800">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityUpdate(item.uniqueId, item.quantity + 1)}
                        className="p-1 rounded-lg bg-white shadow-sm hover:bg-gray-100 transition-colors"
                      >
                        <Plus size={14} className="text-gray-600" />
                      </button>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <div onClick={() => openNoteKeyboard(item.uniqueId)} className="relative cursor-text">
                        <textarea
                          placeholder={t('Ajouter une note...')}
                          value={itemNotes[item.uniqueId] || ''}
                          readOnly
                          className="w-full text-sm px-3 py-0 border border-gray-200 rounded-lg focus:ring-1 focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 resize-none bg-white cursor-text h-8 text-center leading-8"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {showScrollIndicator && (
              <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 pointer-events-none">
                <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-400 opacity-75 animate-bounce" />
              </div>
            )}

            <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-white sticky bottom-0 z-10">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-bold text-gray-800">{t('Total')}</span>
                <span className="text-lg font-bold text-orange-500">
                  {(adjustedTotal !== null ? adjustedTotal : total).toFixed(2)}€
                </span>
              </div>
              <OrderButton
                config={orderBtnDesign}
                fallbackText={t('Commander')}
                price={`${(adjustedTotal !== null ? adjustedTotal : total).toFixed(2)}€`}
                disabled={isEmpty}
                onClick={() => {
                  // v5 BUG FIX CRITIQUE (order_payment_id réutilisé):
                  // Forcer la création d'une NOUVELLE invoice + nouveau cart
                  // pour chaque commande. Sans ça, si invoiceData est encore plein
                  // (ex: client n'a pas cliqué "Terminer" sur la confirmation
                  // précédente), le order_payment_id est réutilisé → NF525 KO.
                  setCartResult(null);
                  setInvoiceData(null);
                  setAdjustedTotal(null);
                  setSubmitError(null);

                  setShowConfirmation(false);
                  setCurrentStep('type');
                  setShowOrderTypeModal((prev) => {
                    if (!prev) {
                      console.log('[v5] Ouverture du modal ordertype - état réinitialisé');
                      return true;
                    }
                    return prev;
                  });
                }}
              />
            </div>
          </>
        )}
      </div>

      {showOrderTypeModal && (
        <Portal>
        <div className="fixed inset-0 flex items-center justify-center z-[9999]" style={{ backgroundColor: 'var(--color-modal-overlay, rgba(0, 0, 0, 0.5))' }}>
          <div
            className={`rounded-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh] shadow-2xl p-6 relative transform transition-transform duration-300 ${
              isTransitioning ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
            }`}
            style={{ backgroundColor: 'var(--color-modal-background, #FFFFFF)' }}
          >
            <button
              onClick={dismissOrderModal} // Utiliser dismissOrderModal au lieu de closeOrderModal
              className="absolute top-0 right-0 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
            >
              <X size={24} />
            </button>

            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-500">
                <span className={currentStep === 'type' ? 'font-bold text-orange-500' : ''}>
                  1. {t('Type de commande')}
                </span>
                <span className={currentStep === 'info' ? 'font-bold text-orange-500' : ''}>
                  2. {t('Informations')}
                </span>
                {currentStep === 'loyalty' && (
                  <span className="font-bold text-orange-500">
                    🎁 {t('Fidélité')}
                  </span>
                )}
                <span className={currentStep === 'payment' ? 'font-bold text-orange-500' : ''}>
                  {currentStep === 'loyalty' ? '4' : '3'}. {t('Paiement')}
                </span>
              </div>
              <div className="h-1 bg-gray-200 rounded-full mt-2">
                <div
                  className="h-1 bg-orange-500 rounded-full transition-all duration-300"
                  style={{
                    width:
                      currentStep === 'type' ? '25%' :
                      currentStep === 'info' ? '50%' :
                      currentStep === 'loyalty' ? '75%' :
                      '100%',
                  }}
                />
              </div>
            </div>

            {currentStep === 'type' && (
              <>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Mode de commande')}</h2>
                <p className="text-gray-600 mb-6">{t('Veuillez sélectionner votre mode de commande')}</p>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <button
                      onClick={() => handleOrderTypeSelection('takeaway')}
                      className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                        orderType === 'takeaway'
                          ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                    >
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <ShoppingBag size={24} className="text-orange-500" />
                      </div>
                      <span className="font-medium text-gray-800 text-lg">{t('À emporter')}</span>
                    </button>
                    <button
                      onClick={() => handleOrderTypeSelection('dine-in')}
                      className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all ${
                        orderType === 'dine-in'
                          ? 'border-orange-500 bg-orange-50 shadow-orange-100'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                    >
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <Menu size={24} className="text-orange-500" />
                      </div>
                      <span className="font-medium text-gray-800 text-lg">{t('Sur place')}</span>
                    </button>
                  </div>
                  <div className="flex justify-center mt-6">
                    <Lottie
                      animationData={leftHandAnimation}
                      loop={true}
                      isPaused={!isTabVisible}
                      style={{ width: 300, height: 300 }}
                    />
                  </div>
                </div>
              </>
            )}

            {currentStep === 'info' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Entrez vos informations')}</h2>
                {orderType === 'dine-in' && (
                  <div>
                    <div onClick={() => handleFieldFocus('table')} className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Menu size={20} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={displayTableNumber}
                        readOnly
                        placeholder={t('Numéro de table')}
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 ${
                          activeField === 'table' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
                        }`}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <div onClick={() => handleFieldFocus('name')} className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User size={20} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={displayCustomerName}
                      readOnly
                      placeholder={t('Votre nom', {
                        optional: orderType === 'takeaway' ? t('Optionnel') : '',
                      })}
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 ${
                        activeField === 'name' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
                      }`}
                    />
                  </div>
                </div>

                {/* v5 fidélité: champ téléphone optionnel (si activé en admin) */}
                {loyaltySettings.phonePromptEnabled && (
                  <div>
                    <div className="flex items-center gap-2 mb-1.5 px-1">
                      <span className="text-base">🎁</span>
                      <p className="text-xs text-gray-600">
                        {t("Téléphone pour cumuler des points de fidélité (facultatif)")}
                      </p>
                    </div>
                    <div onClick={() => handleFieldFocus('phone')} className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 text-base">
                        📱
                      </div>
                      <input
                        type="text"
                        value={displayCustomerPhone}
                        readOnly
                        placeholder={t("06 XX XX XX XX (optionnel)")}
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-2xl transition-colors outline-none text-lg font-semibold text-gray-800 bg-gray-50 ${
                          activeField === 'phone' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
                        }`}
                      />
                      {displayCustomerPhone && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCustomerPhone('');
                            setDisplayCustomerPhone('');
                          }}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                          title={t("Effacer")}
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {showNumberKeyboard && (
                  <div className="mt-4 space-y-2" style={{ backgroundColor: 'var(--color-keypad-background, #FFFFFF)' }}>
                    {numberRows.map((row, rowIndex) => (
                      <div key={rowIndex} className="flex justify-center gap-2">
                        {row.map((number) => (
                          <button
                            key={number}
                            onClick={() => handleNumberPress(number)}
                            className="w-32 h-24 rounded-lg text-lg font-semibold transition-all active:transform active:scale-95"
                            style={
                              number === 'backspace' || number === 'clear'
                                ? { backgroundColor: 'var(--color-back-button, #6B7280)', color: 'var(--color-back-button-text, #FFFFFF)' }
                                : { backgroundColor: 'var(--color-keypad-button, #F97316)', color: 'var(--color-keypad-button-text, #FFFFFF)' }
                            }
                          >
                            {number === 'backspace' ? (
                              <Delete size={20} className="mx-auto" />
                            ) : number === 'clear' ? (
                              t('Clear')
                            ) : (
                              number
                            )}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {showLetterKeyboard && (
                  <div className="mt-4 space-y-2" style={{ backgroundColor: 'var(--color-keypad-background, #FFFFFF)' }}>
                    {letterRows.map((row, rowIndex) => (
                      <div key={rowIndex} className="flex justify-center gap-1">
                        {row.map((letter) => (
                          <button
                            key={letter}
                            onClick={() => handleLetterPress(letter)}
                            className="w-14 h-20 rounded-lg text-base font-semibold transition-all active:transform active:scale-95 flex-shrink-0"
                            style={{ backgroundColor: 'var(--color-keypad-button, #F97316)', color: 'var(--color-keypad-button-text, #FFFFFF)' }}
                          >
                            {letter}
                          </button>
                        ))}
                      </div>
                    ))}
                    <div className="flex justify-center gap-2 mt-2">
                      <button
                        onClick={() => handleLetterPress('clear')}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all active:transform active:scale-95"
                        style={{ backgroundColor: 'var(--color-back-button, #6B7280)', color: 'var(--color-back-button-text, #FFFFFF)' }}
                      >
                        {t('Effacer')}
                      </button>
                      <button
                        onClick={() => handleLetterPress('space')}
                        className="px-8 py-2 rounded-lg text-sm font-semibold transition-all active:transform active:scale-95"
                        style={{ backgroundColor: 'var(--color-keypad-button, #F97316)', color: 'var(--color-keypad-button-text, #FFFFFF)' }}
                      >
                        {t('Espace')}
                      </button>
                      <button
                        onClick={() => handleLetterPress('backspace')}
                        className="px-4 py-2 rounded-lg transition-all active:transform active:scale-95"
                        style={{ backgroundColor: 'var(--color-back-button, #6B7280)', color: 'var(--color-back-button-text, #FFFFFF)' }}
                      >
                        <Delete size={20} />
                      </button>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => handleNextStep()}
                  className="w-full mt-6 py-3 rounded-2xl font-semibold transition-colors"
                  style={{ backgroundColor: 'var(--color-confirm-button, #10B981)', color: 'var(--color-confirm-button-text, #FFFFFF)' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('Chargement') : t('Continuer vers le paiement')}
                </button>
              </div>
            )}

            {/* v5 fidélité: étape loyalty INTÉGRÉE au cadre principal (pas un popup séparé).
                Affichée entre 'info' et 'payment', dans le même style que les autres étapes. */}
            {currentStep === 'loyalty' && (
              <LoyaltyStepContent
                phone={formatCustomerPhone(customerPhone)}
                cartTotal={total}
                onConfirm={(points, discount) => {
                  console.log('[LoyaltyRedeem] User confirme réduction:', { points, discount });
                  setLoyaltyPointsUsed(points);
                  setLoyaltyDiscount(discount);
                  // handleNextStep(true) traitera 'loyalty' comme 'info' avec skipLoyaltyCheck
                  // → création cart+invoice + apply_loyalty_discount + transition vers payment
                  // SANS repasser visuellement par 'info' (pas de clignotement)
                  setTimeout(() => handleNextStep(true), 50);
                }}
                onSkip={() => {
                  console.log('[LoyaltyRedeem] User refuse la réduction');
                  setLoyaltyPointsUsed(0);
                  setLoyaltyDiscount(0);
                  // Idem : handleNextStep(true) → création cart+invoice (sans réduction) → payment
                  setTimeout(() => handleNextStep(true), 50);
                }}
              />
            )}

            {currentStep === 'payment' && (
              <div className="space-y-6">
                {/* v5 fidélité REDEEM: récap de la réduction si applicable */}
                {loyaltyDiscount > 0 && (
                  <LoyaltyDiscountRecap
                    pointsUsed={loyaltyPointsUsed}
                    discount={loyaltyDiscount}
                    cartTotal={total}
                    finalTotal={Math.max(0, total - loyaltyDiscount)}
                    onCancel={() => {
                      console.log('[LoyaltyRedeem] User annule la réduction depuis le récap');
                      setLoyaltyPointsUsed(0);
                      setLoyaltyDiscount(0);
                      // Recréer cart+invoice avec total original
                      setCartResult(null);
                      setInvoiceData(null);
                      setAdjustedTotal(null);
                      // Revenir à info pour relancer le flow propre
                      setCurrentStep('info');
                      setShowOrderForm(true);
                      setShowPaymentForm(false);
                    }}
                  />
                )}
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Mode de paiement')}</h2>
                <p className="text-gray-600 mb-6">{t('Choisissez votre méthode de paiement préférée')}</p>
                <div className="flex flex-col items-center -mb-4">
                  <Lottie
                    animationData={leftHandAnimation}
                    loop={true}
                    isPaused={!isTabVisible}
                    style={{ width: 120, height: 120 }}
                  />
                  <span className="text-orange-500 font-semibold text-lg animate-pulse -mt-2">{t('Cliquez pour valider')}</span>
                </div>
                <div
                  className={`grid ${
                    (paymentSettings.enableCashPayment && !paymentSettings.enableCardPayment) ||
                    (!paymentSettings.enableCashPayment && paymentSettings.enableCardPayment)
                      ? 'grid-cols-1 max-w-md mx-auto'
                      : 'grid-cols-2'
                  } gap-6`}
                >
                  {paymentSettings.enableCashPayment && (
                    <div className="relative rounded-2xl p-[3px] bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 bg-[length:200%_100%] animate-gradient-x shadow-[0_0_20px_rgba(249,115,22,0.4)] hover:shadow-[0_0_30px_rgba(249,115,22,0.6)] transition-shadow duration-300">
                      <button
                        onClick={() => handlePaymentMethodSelection('cash')}
                        disabled={isSubmitting}
                        className={`w-full flex flex-col items-center gap-3 p-6 rounded-2xl transition-all ${
                          paymentMethod === 'cash'
                            ? 'bg-orange-50'
                            : 'bg-white hover:bg-orange-50'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full flex items-center justify-center">
                          <Wallet size={28} className="text-orange-500" />
                        </div>
                        <div className="text-center">
                          <span className="font-semibold text-gray-800 text-lg block">{t('Paiement à la caisse')}</span>
                          <span className="text-sm text-gray-500">{t('Payez directement au comptoir')}</span>
                        </div>
                      </button>
                    </div>
                  )}
                  {paymentSettings.enableCardPayment && (
                    <div className="relative rounded-2xl p-[3px] bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-[length:200%_100%] animate-gradient-x shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-shadow duration-300">
                      <button
                        onClick={() => handlePaymentMethodSelection('card')}
                        disabled={isSubmitting}
                        className={`w-full flex flex-col items-center gap-3 p-6 rounded-2xl transition-all ${
                          paymentMethod === 'card'
                            ? 'bg-blue-50'
                            : 'bg-white hover:bg-blue-50'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                          <CreditCard size={28} className="text-blue-500" />
                        </div>
                        <div className="text-center">
                          <span className="font-semibold text-gray-800 text-lg block">{t('Paiement par carte')}</span>
                          <span className="text-sm text-gray-500">{t('Terminal de paiement')}</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
                {showPaymentProcessor && paymentMethod === 'card' && (
                  <div className="mt-6 p-6 bg-gray-50 rounded-2xl">
                    <PaymentProcessor
                      amount={adjustedTotal !== null ? adjustedTotal : total}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      onCancel={handlePaymentCancel}
                      autoStart={true}
                    />
                  </div>
                )}
                {submitError && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-center gap-2 text-red-600">
                    <AlertCircle size={20} />
                    <p className="text-sm">{submitError}</p>
                    <button
                      onClick={() => {
                        setSubmitError(null);
                        // v5: réessayer dans le mode actuellement sélectionné
                        // (ou cash par défaut si aucun n'est sélectionné)
                        const retryMethod: 'cash' | 'card' =
                          paymentMethod || 'cash';
                        handlePaymentMethodSelection(retryMethod);
                      }}
                      className="ml-4 py-1 px-3 rounded-lg transition-colors"
                      style={{ backgroundColor: 'var(--color-confirm-button, #10B981)', color: 'var(--color-confirm-button-text, #FFFFFF)' }}
                    >
                      {t('Réessayer')}
                    </button>
                  </div>
                )}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handlePreviousStep}
                    disabled={isSubmitting}
                    className="flex-1 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--color-back-button, #6B7280)', color: 'var(--color-back-button-text, #FFFFFF)' }}
                  >
                    {t('Retour')}
                  </button>
                  {submitError && (
                    <button
                      onClick={closeOrderModal} // Vider le panier ici
                      disabled={isSubmitting}
                      className="flex-1 py-3 rounded-xl font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      {t('Annuler la commande')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        </Portal>
      )}

      {showNoteKeyboard && activeNoteId && (
        <Portal>
        <div className="fixed inset-0 flex items-center justify-center z-[9999]" style={{ backgroundColor: 'var(--color-modal-overlay, rgba(0, 0, 0, 0.5))' }}>
          <div className="rounded-2xl w-full max-w-md overflow-hidden shadow-2xl p-6 relative" style={{ backgroundColor: 'var(--color-modal-background, #FFFFFF)' }}>
            <button
              onClick={closeNoteKeyboard}
              className="absolute top-0 right-0 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
            >
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Note spéciale')}</h2>
            <p className="text-gray-600 mb-6">{t('Ajoutez une note pour ce produit')}</p>
            <div className="relative">
              <textarea
                value={itemNotes[activeNoteId] || ''}
                readOnly
                placeholder={t('Votre note...')}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring focus:ring-orange-200 transition-colors outline-none text-lg font-medium text-gray-800 bg-gray-50 resize-none"
                rows={3}
              />
              <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                {(itemNotes[activeNoteId] || '').length}/200
              </div>
            </div>
            <div className="mt-4 space-y-2" style={{ backgroundColor: 'var(--color-keypad-background, #FFFFFF)' }}>
              {letterRows.map((row, rowIndex) => (
                <div key={rowIndex} className="flex justify-center gap-1">
                  {row.map((letter) => (
                    <button
                      key={letter}
                      onClick={() => handleNoteKeyPress(letter)}
                      className="w-9 h-9 rounded-lg text-base font-semibold transition-all active:transform active:scale-95"
                      style={{ backgroundColor: 'var(--color-keypad-button, #F97316)', color: 'var(--color-keypad-button-text, #FFFFFF)' }}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
              ))}
              <div className="flex justify-center gap-2 mt-2">
                <button
                  onClick={() => handleNoteKeyPress('clear')}
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-all active:transform active:scale-95"
                  style={{ backgroundColor: 'var(--color-back-button, #6B7280)', color: 'var(--color-back-button-text, #FFFFFF)' }}
                >
                  {t('Effacer')}
                </button>
                <button
                  onClick={() => handleNoteKeyPress('space')}
                  className="px-8 py-2 rounded-lg transition-all active:transform active:scale-95"
                  style={{ backgroundColor: 'var(--color-keypad-button, #F97316)', color: 'var(--color-keypad-button-text, #FFFFFF)' }}
                >
                  {t('Espace')}
                </button>
                <button
                  onClick={() => handleNoteKeyPress('backspace')}
                  className="px-4 py-2 rounded-lg transition-all active:transform active:scale-95"
                  style={{ backgroundColor: 'var(--color-back-button, #6B7280)', color: 'var(--color-back-button-text, #FFFFFF)' }}
                >
                  <Delete size={20} />
                </button>
              </div>
            </div>
            <button
              onClick={closeNoteKeyboard}
              className="w-full mt-6 py-3 rounded-xl font-semibold transition-colors"
              style={{ backgroundColor: 'var(--color-confirm-button, #10B981)', color: 'var(--color-confirm-button-text, #FFFFFF)' }}
            >
              {t('Valider la note')}
            </button>
          </div>
        </div>
        </Portal>
      )}

      {invoiceData && showConfirmation && (
        <OrderConfirmation
          isOpen={showConfirmation}
          onClose={handleConfirmationClose}
          orderType={orderType || 'takeaway'}
          items={items.map(item => ({
            ...item,
            note: itemNotes[item.uniqueId] || ''
          }))}
          categories={categories}
          customerName={customerName}
          tableNumber={tableNumber}
          paymentMethod={paymentMethod || 'cash'}
          invoiceData={invoiceData}
          printSettings={printSettings}
        />
      )}

      {/* v5 fidélité: message gentil par-dessus la confirmation si la réduction
          n'a pas pu être appliquée (apply_loyalty_discount a échoué).
          Le client a payé le plein tarif → on lui dit d'aller voir la caisse. */}
      {loyaltyApplyFailedMessage && showConfirmation && (
        <div className="fixed inset-x-0 top-8 z-[60] flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto max-w-md w-full bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl shadow-2xl p-4 flex items-start gap-3 animate-bounce-in">
            <div className="text-2xl flex-shrink-0">💛</div>
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900 mb-1">
                {t("Fidélité — petit souci")}
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">
                {loyaltyApplyFailedMessage}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setLoyaltyApplyFailedMessage('')}
              className="text-amber-600 hover:text-amber-800 text-lg flex-shrink-0 -mt-1"
              aria-label={t('Fermer')}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <MinimumOrderModal
        isOpen={showMinimumOrderModal}
        onClose={() => setShowMinimumOrderModal(false)}
        minimumAmount={minimumOrderInfo.minimum}
        currentAmount={minimumOrderInfo.current}
        currency="€"
      />
    </div>
  );
};

export default Cart;